import { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import {
  AuthButton,
  AuthError,
  AuthInput,
  AuthPageShell,
  AuthSuccess,
} from './AuthPageLayout';

interface ForgotPasswordProps {
  onBack: () => void;
  onSuccess: () => void;
}

export default function ForgotPassword({ onBack, onSuccess }: ForgotPasswordProps) {
  const [step, setStep] = useState<'email' | 'reset'>('email');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const { forgotPassword, resetPassword } = useAuth();

  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  useEffect(() => {
    if (step === 'reset') {
      document.getElementById('otp')?.focus();
    }
  }, [step]);

  const handleRequestOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await forgotPassword(email);
      setSuccess('Password reset code sent to your email.');
      setStep('reset');
      setResendTimer(60);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to send reset code. Please check your email.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (otp.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      await resetPassword(email, otp, newPassword);
      setSuccess('Password reset successfully! Redirecting to sign in…');
      setTimeout(() => {
        onSuccess();
      }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Invalid or expired OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setError('');
    setSuccess('');
    setResendLoading(true);

    try {
      await forgotPassword(email);
      setSuccess('A new reset code has been sent to your email.');
      setResendTimer(60);
      setOtp('');
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to resend code. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  const footer = (
    <p className="text-center text-sm text-slate-600">
      Remember your password?{' '}
      <button
        type="button"
        onClick={onBack}
        className="font-semibold text-[#008540] transition hover:text-[#097a34] hover:underline"
      >
        Sign in
      </button>
    </p>
  );

  if (step === 'email') {
    return (
      <AuthPageShell
        title="Reset password"
        subtitle="Enter your email and we'll send you a reset code."
        badge="Account recovery"
        backLabel="Back to login"
        onBack={onBack}
        footer={footer}
      >
        {error && <AuthError message={error} />}
        {success && <AuthSuccess message={success} />}

        <form onSubmit={handleRequestOTP} className="space-y-3">
          <AuthInput
            id="email"
            label="Email address"
            type="email"
            icon="ri-mail-line"
            value={email}
            onChange={setEmail}
            placeholder="you@example.com"
            autoComplete="email"
            required
          />

          <AuthButton type="submit" loading={loading}>
            {loading ? 'Sending…' : 'Send reset code'}
          </AuthButton>
        </form>
      </AuthPageShell>
    );
  }

  return (
    <AuthPageShell
      title="Set new password"
      subtitle="Enter the 6-digit code from your email."
      badge="Account recovery"
      backLabel="Back to login"
      onBack={onBack}
      footer={footer}
    >
      <p className="mb-3 text-center text-xs text-slate-500">
        Code sent to{' '}
        <span className="font-semibold text-[#008540]">{email}</span>
      </p>

      {error && <AuthError message={error} />}
      {success && <AuthSuccess message={success} />}

      <form onSubmit={handleResetPassword} className="space-y-3">
        <div>
          <label htmlFor="otp" className="mb-1 block text-xs font-medium text-slate-700">
            Reset code
          </label>
          <input
            id="otp"
            type="text"
            inputMode="numeric"
            value={otp}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '');
              if (value.length <= 6) setOtp(value);
            }}
            maxLength={6}
            required
            autoComplete="one-time-code"
            placeholder="000000"
            className="auth-field w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-center text-xl font-semibold tracking-[0.35em] text-gray-900 shadow-sm outline-none transition placeholder:tracking-normal placeholder:text-slate-400 focus:border-[#008540] focus:ring-2 focus:ring-[#008540]/20"
          />
        </div>

        <AuthInput
          id="newPassword"
          label="New password"
          type="password"
          icon="ri-lock-2-line"
          value={newPassword}
          onChange={setNewPassword}
          placeholder="Min 8 characters"
          autoComplete="new-password"
          passwordToggle
          minLength={8}
          required
        />

        <AuthInput
          id="confirmPassword"
          label="Confirm new password"
          type="password"
          icon="ri-lock-2-line"
          value={confirmPassword}
          onChange={setConfirmPassword}
          placeholder="Re-enter password"
          autoComplete="new-password"
          passwordToggle
          minLength={8}
          required
        />

        <AuthButton type="submit" loading={loading}>
          {loading ? 'Resetting…' : 'Reset password'}
        </AuthButton>
      </form>

      <div className="mt-4 space-y-3 text-center">
        <p className="text-xs text-slate-500">
          Didn&apos;t receive the code?{' '}
          <button
            type="button"
            onClick={handleResendOTP}
            disabled={resendLoading || resendTimer > 0}
            className="font-semibold text-emerald-700 transition hover:text-emerald-800 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
          >
            {resendLoading ? 'Sending…' : resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend code'}
          </button>
        </p>

        <div className="rounded-lg border border-sky-200/80 bg-sky-50/80 px-3 py-2 text-[11px] text-sky-800">
          <i className="ri-information-line mr-1" aria-hidden />
          The reset code expires in 10 minutes.
        </div>
      </div>
    </AuthPageShell>
  );
}
